import React from 'react';
import { ArrowRightIcon, MonitorIcon, RotateCcwIcon, ShieldCheckIcon, SmartphoneIcon, UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { DmpLogo } from '../components/brand/DmpLogo';
import { BdFlag } from '../components/brand/BdFlag';
import { MEDIA } from '../data/media';
import { scenarios, type AppMode } from '../data/scenarios';
import { useStore } from '../contexts/StoreContext';
import { bn } from '../utils/bn';
const APPS: {
  mode: AppMode;
  title: string;
  desc: string;
  users: string;
  icon: typeof SmartphoneIcon;
  image: string;
}[] = [{
  mode: 'field',
  title: 'ডিএমপি কর্মকর্তা অ্যাপ',
  desc: 'হেফাজত নিবন্ধন, PCR, রসিদ, বারকোড প্রিন্ট, পিকআপ, স্টেশনে গ্রহণ ও QR-ভিত্তিক অবমুক্তি।',
  users: 'ট্রাফিক সার্জেন্ট · রেকার অপারেটর · ডাম্পিং স্টেশন কর্মকর্তা',
  icon: SmartphoneIcon,
  image: MEDIA.wrecker
}, {
  mode: 'citizen',
  title: 'যানবাহন মালিক অ্যাপ',
  desc: 'PCR অনুসন্ধান, কাগজপত্র জমা, জরিমানা পরিশোধ, অগ্রগতি অনুসরণ ও অবমুক্তি পত্রের তথ্য।',
  users: 'যানবাহন মালিক · অনুমোদিত প্রতিনিধি',
  icon: UserIcon,
  image: MEDIA.car
}, {
  mode: 'admin',
  title: 'ওয়েব অ্যাডমিন পোর্টাল',
  desc: 'কেস ব্যবস্থাপনা, যাচাই, পেমেন্ট সমন্বয়, ই-নথি ও নোটশিট, বহুস্তর অনুমোদন, অবমুক্তি পত্র ও প্রতিবেদন।',
  users: 'যাচাইকারী · নথি প্রস্তুতকারী · অনুমোদনকারী · স্বাক্ষরকারী · অ্যাডমিন',
  icon: MonitorIcon,
  image: MEDIA.yard
}];
const APP_NAME: Record<AppMode, string> = {
  field: 'কর্মকর্তা অ্যাপ',
  citizen: 'মালিক অ্যাপ',
  admin: 'ওয়েব পোর্টাল'
};
export function Landing({
  onOpen
}: {
  onOpen: (m: AppMode) => void;
}) {
  const {
    resetDemo,
    cases
  } = useStore();
  return <div className="min-h-full w-full bg-canvas" data-id="element-1728" data-mp="c7ofx00wzzr0">
      <header className="bg-navy text-white" data-id="element-1729" data-mp="c6dfna6oha4n">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3" data-id="element-1730" data-mp="79myejj508hn">
          <div className="flex items-center gap-3" data-id="element-1731" data-mp="14o90v2l1o59n">
            <DmpLogo size={44} tone="light" data-id="element-1732" data-mp="14ptrwi85h62r" />
            <div data-id="element-1733" data-mp="zx917pkjj93g">
              <p className="text-base font-bold leading-tight" data-id="element-1734" data-mp="zwr8h2x37d0a">ঢাকা মেট্রোপলিটন পুলিশ (ডিএমপি)</p>
              <p className="text-xs text-white/75" data-id="element-1735" data-mp="1itrgcdwcs0fy">যানবাহন ডাম্পিং ও অবমুক্তকরণ ব্যবস্থাপনা সিস্টেম</p>
            </div>
          </div>
          <BdFlag width={34} data-id="element-1736" data-mp="1nscdu5k8060p" />
        </div>
      </header>

      <section className="relative" data-id="element-1737" data-mp="1a2iolm4szjqg">
        <img src={MEDIA.police} alt="ঢাকার সড়কে দায়িত্বরত ট্রাফিক সার্জেন্ট" className="h-[260px] w-full object-cover" data-id="element-1738" data-mp="1a1h3spxzjivj" />
        <div className="absolute inset-0 bg-navy-950/70" data-id="element-1739" data-mp="1e0ybzr4squ4e" />
        <div className="absolute inset-0" data-id="element-1740" data-mp="102rrrdsrssn7">
          <div className="mx-auto flex h-full max-w-6xl flex-col justify-center px-5 text-white" data-id="element-1741" data-mp="152mklmw3vsaw">
            <h1 className="text-3xl md:text-4xl font-bold leading-tight max-w-3xl" data-id="element-1742" data-mp="r2v1j7qqflq1">যানবাহন ডাম্পিং ও অবমুক্তকরণ ব্যবস্থাপনা সিস্টেম</h1>
            <p className="mt-2 max-w-2xl text-white/85" data-id="element-1743" data-mp="v5hwvimm3orz">হাতে লেখা হেফাজত স্লিপ, কাগজের নথি চলাচল ও কাগুজে অবমুক্তি যাচাইয়ের পরিবর্তে কেন্দ্রীয়, অনুসরণযোগ্য ডিজিটাল প্রক্রিয়া — হেফাজত থেকে হস্তান্তর পর্যন্ত।</p>
            <p className="mt-3 text-xs text-white/70" data-id="element-1744" data-mp="2ph6gvz7pahj8">English Reference: DMP Digital Vehicle Dumping & Release Management System</p>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-6xl px-5 py-8" data-id="element-1745" data-mp="9uby3t3tnua8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4" data-id="element-1746" data-mp="9rg2ibfg5hoy">
          <div data-id="element-1747" data-mp="nmidve32bz95">
            <h2 className="text-xl font-bold text-ink" data-id="element-1748" data-mp="nn0lf6v6dzwd">ডেমোর জন্য অ্যাপ্লিকেশন নির্বাচন করুন</h2>
            <p className="text-sm text-ink-muted" data-id="element-1749" data-mp="is4e6zhdb5yo">তিনটি অ্যাপই একই কেস রেকর্ড ব্যবহার করে — এক অ্যাপের পরিবর্তন অন্যটিতে সঙ্গে সঙ্গে দেখা যায়।</p>
          </div>
          <button className="btn btn-outline" onClick={() => {
          resetDemo();
          toast.success('ডেমো তথ্য প্রাথমিক অবস্থায় ফিরিয়ে আনা হয়েছে।');
        }} data-id="element-1750" data-mp="siz3ydttonov">
            <RotateCcwIcon className="h-4 w-4" data-id="element-1751" data-mp="11k6wsgdkkwbk" /> ডেমো রিসেট
          </button>
        </div>

        <div className="grid gap-5 md:grid-cols-3" data-id="element-1752" data-mp="38amwzy5ptw2e">
          {APPS.map(a => <button key={a.mode} onClick={() => onOpen(a.mode)} className="card group flex flex-col overflow-hidden text-left transition-shadow duration-200 hover:shadow-pop focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy" data-id="element-1753" data-mp="33c3c812uyqbm">
              <div className="relative h-36" data-id="element-1754" data-mp="39r0o8oi46z5c">
                <img src={a.image} alt="" className="h-full w-full object-cover" data-id="element-1755" data-mp="3erez0eb3on72" />
                <span className="absolute left-3 top-3 flex h-10 w-10 items-center justify-center rounded-lg bg-white text-navy shadow-card" data-id="element-1756" data-mp="3epc3jer6i1uy">
                  <a.icon className="h-5 w-5" data-id="element-1757" data-mp="2m3iuwoj7bt09" />
                </span>
              </div>
              <div className="flex flex-1 flex-col p-5" data-id="element-1758" data-mp="2r1awka9nethn">
                <h3 className="text-lg font-bold text-navy" data-id="element-1759" data-mp="2qzrfwik2ygfc">{a.title}</h3>
                <p className="mt-1.5 text-sm text-ink-muted" data-id="element-1760" data-mp="2w05g5effwm2t">{a.desc}</p>
                <p className="mt-3 text-xs text-ink-soft" data-id="element-1761" data-mp="30rx6poylirsy">{a.users}</p>
                <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-bdgreen" data-id="element-1762" data-mp="23c9kmd1lx07m">
                  প্রবেশ করুন <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5" data-id="element-1763" data-mp="28akugnrwjapu" />
                </span>
              </div>
            </button>)}
        </div>

        <section className="mt-10 grid gap-6 lg:grid-cols-[1fr_320px]" data-id="element-1764" data-mp="1vsh0bn1vbde1">
          <div className="card p-5" data-id="element-1765" data-mp="1qtdw24tppb74">
            <h2 className="text-lg font-bold text-ink" data-id="element-1766" data-mp="1qscle1glyqpx">এন্ড-টু-এন্ড ডেমো পরিস্থিতি</h2>
            <p className="text-sm text-ink-muted mb-4" data-id="element-1767" data-mp="1lvch9w3bjy1g">উপস্থাপনার ক্রম — প্রতিটি ধাপ শেয়ার্ড কেস ডেটা হালনাগাদ করে।</p>
            <ol className="divide-y divide-line" data-id="element-1768" data-mp="1huiqmuyhs54v">
              {scenarios.map((s, i) => <li key={s.title} className="flex items-start gap-4 py-3" data-id="element-1769" data-mp="1d1h9egatbti4" data-mpi={i}>
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-navy text-xs font-bold text-white" data-id="element-1770" data-mp="182d0zrvppzsu">{bn(i + 1)}</span>
                  <div className="flex-1 min-w-0" data-id="element-1771" data-mp="1328jbyj2iz11">
                    <p className="font-semibold text-ink" data-id="element-1772" data-mp="y3x8bkh1eih8">{s.title} <span className="text-xs font-normal text-ink-muted" data-id="element-1773" data-mp="y6t535h5f143">— {s.role}</span></p>
                    <p className="text-sm text-ink-muted" data-id="element-1774" data-mp="u5r4olbhwj0j">{s.steps}</p>
                  </div>
                  <button onClick={() => onOpen(s.app)} className="btn btn-sm btn-outline shrink-0" data-id="element-1775" data-mp="jke27a2g5tcd">{APP_NAME[s.app]}</button>
                </li>)}
            </ol>
          </div>
          <aside className="space-y-4" data-id="element-1776" data-mp="ajfwmncd1dro">
            <div className="card p-5" data-id="element-1777" data-mp="fg68eyh4tn7a">
              <div className="flex items-center gap-2 text-bdgreen" data-id="element-1778" data-mp="fh7wc3j8y46f"><ShieldCheckIcon className="h-5 w-5" data-id="element-1779" data-mp="136is16lqp8dp" /><h3 className="font-bold" data-id="element-1780" data-mp="137jzl13jrcqo">প্রোটোটাইপ ঘোষণা</h3></div>
              <ul className="mt-2 space-y-1.5 text-sm text-ink-muted list-disc pl-4" data-id="element-1781" data-mp="178dpu14gp3mq">
                <li data-id="element-1782" data-mp="tgpe3yy4kh6a">সকল তথ্য কাল্পনিক ও নমুনা।</li>
                <li data-id="element-1783" data-mp="tfxttk9za5vb">জরিমানার পরিমাণ উদাহরণমাত্র — অনুমোদিত ডিএমপি হার নয়।</li>
                <li data-id="element-1784" data-mp="yeijiprlt75i">পেমেন্ট, SMS, ব্লুটুথ প্রিন্ট, ক্যামেরা স্ক্যান ও ডিজিটাল স্বাক্ষর সিমুলেটেড; কোনো লাইভ ইন্টিগ্রেশন সক্রিয় নয়।</li>
                <li data-id="element-1785" data-mp="1m31rt5anljxl">লোগো স্থানটি অফিসিয়াল লোগো আপলোডের জন্য সংরক্ষিত।</li>
              </ul>
            </div>
            <div className="card p-5" data-id="element-1786" data-mp="1qxo5jf47joku">
              <p className="text-sm text-ink-muted" data-id="element-1787" data-mp="209vc8jr58swl">বর্তমান ডেমো রেকর্ড</p>
              <p className="text-3xl font-bold text-navy" data-id="element-1788" data-mp="1w91qnx16bq6v">{bn(cases.length)} <span className="text-base font-semibold text-ink-muted" data-id="element-1789" data-mp="1w8ta9v5ue2qx">PCR কেস</span></p>
              <p className="text-xs text-ink-soft mt-1" data-id="element-1790" data-mp="29zyelak5nj7l">ব্রাউজার সেশন চলাকালীন পরিবর্তন সংরক্ষিত থাকে।</p>
            </div>
          </aside>
        </section>
      </main>
    </div>;
}